/**
 * DocumentHead — minimal, dependency-free per-page SEO head manager.
 *
 * Sets <title>, <meta name="description">, and the core Open Graph tags
 * on mount, then restores whatever was in index.html on unmount. Used on
 * public landing routes only. No react-helmet dependency.
 *
 * Usage:
 *   <DocumentHead
 *     title="QuickInbox — Anonymous Email Made Simple"
 *     description="Create anonymous email aliases in seconds…"
 *     path="/"
 *   />
 */
import { useEffect } from "react";

const SITE_ORIGIN = "https://quickinbox.app";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content);
  return el;
}

function upsertLinkCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function DocumentHead({ title, description, path = "/" }) {
  useEffect(() => {
    const prevTitle = document.title;
    const canonicalUrl = `${SITE_ORIGIN}${path}`;

    if (title) document.title = title;

    // Description
    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description || "",
    });

    // Canonical URL for this page
    upsertLinkCanonical(canonicalUrl);

    // Open Graph
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title || "" });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description || "" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "QuickInbox" });

    // Twitter Card (minimal, piggybacks on OG)
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title || "" });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description || "" });

    return () => {
      // Restore title so SPA navigations back to a non-landing route don't keep stale title.
      if (title) document.title = prevTitle;
    };
  }, [title, description, path]);

  return null;
}
